import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { FormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Subject, filter, finalize, take, takeUntil } from 'rxjs';
import { FirmSettings } from 'src/app/core/entity/firm.settings';
import {
  ASSIGNMENT_FIRM_DETAIL_FIELDS,
  UserAssignmentFirmDetails,
} from 'src/app/core/entity/user-assignment-firm-details';
import { UserProperties, UserPropertiesPatch } from 'src/app/core/entity/user-properties';
import { AppState } from 'src/app/core/state/appState';
import { AuthSelectors } from 'src/app/core/state/auth/auth.selectors';
import { UserTypeWithFirm } from 'src/app/core/state/types';
import { UserActions } from 'src/app/core/state/users/users.actions';

@Component({
  selector: 'app-firm-assignment-settings',
  templateUrl: './firm-assignment-settings.component.html',
  styleUrls: ['./firm-assignment-settings.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FirmAssignmentSettingsComponent implements OnInit, OnDestroy {
  assignmentSettingsForm: UntypedFormGroup;
  resetDialog: boolean;
  maxLength = 500;
  userFirmDetailsForm = this.formBuilder.nonNullable.group({
    enabled: false,
    details: this.formBuilder.nonNullable.group({
      name: '',
      corporateIdentity: '',
      address: '',
      zipcode: '',
      city: '',
      phone: '',
      email: ['', Validators.email],
    }),
  });
  loadingUserFirmDetails = signal(true);
  userFirmDetailsLoaded = signal(false);
  savingUserFirmDetails = signal(false);
  userFirmDetailsError = signal<string | null>(null);

  get canSaveUserFirmDetails(): boolean {
    return this.userFirmDetailsLoaded() && !this.loadingUserFirmDetails() && !this.savingUserFirmDetails();
  }

  @Input() set firmAssignmentData$(firmAssignmentData$: any) {
    const { ...firmAssignmentData } = firmAssignmentData$;
    this.assignmentSettingsForm.patchValue(this.booleanifyFormValues(firmAssignmentData));
  }

  @Output() updateFirmAssignmentData = new EventEmitter();
  @Output() resetFirmAssignmentData = new EventEmitter();

  private onDestroyBS: Subject<boolean>;
  private currentUser: UserTypeWithFirm;
  private savedUserProperties: UserProperties = {};

  constructor(
    private formBuilder: FormBuilder,
    private store: Store<AppState>,
    private actions$: Actions,
  ) {
    this.onDestroyBS = new Subject();
    this.setAssignmentSettingsForm();
  }

  ngOnInit(): void {
    this.loadUserFirmDetails();
  }

  ngOnDestroy() {
    this.onDestroyBS.next(true);
    this.onDestroyBS.complete();
  }

  loadUserFirmDetails(): void {
    this.loadingUserFirmDetails.set(true);
    this.userFirmDetailsError.set(null);
    this.store
      .select(AuthSelectors.selectAuthUser)
      .pipe(
        filter((user): user is UserTypeWithFirm => !!user),
        take(1),
        takeUntil(this.onDestroyBS),
        finalize(() => this.loadingUserFirmDetails.set(false)),
      )
      .subscribe({
        next: (user) => {
          this.currentUser = user;
          this.applyUserProperties(user.properties ?? {});
          this.userFirmDetailsLoaded.set(true);
        },
        error: () => this.userFirmDetailsError.set('Kunde inte hämta dina egna byråuppgifter. Försök igen.'),
      });
  }

  private applyUserProperties(properties: UserProperties): void {
    this.savedUserProperties = { ...properties };
    this.userFirmDetailsForm.reset({ enabled: this.hasUserFirmDetails(properties) });
    for (const field of ASSIGNMENT_FIRM_DETAIL_FIELDS) {
      const key: keyof UserAssignmentFirmDetails = `assignment.${field}`;
      const value = properties[key];
      this.userFirmDetailsForm.controls.details.controls[field].setValue(value ?? '');
    }
  }

  private hasUserFirmDetails(properties: UserProperties): boolean {
    return ASSIGNMENT_FIRM_DETAIL_FIELDS.some((field) => typeof properties[`assignment.${field}`] === 'string');
  }

  private setAssignmentSettingsForm(): void {
    this.assignmentSettingsForm = this.formBuilder.group({
      other_condition_text: [''],
      other_terms_text: [''],
      srf_text: [''],
      srf_or_far: [''],
      far_text: [''],
      use_far: [false],
      use_srf: [false],
      notice_period: [''],
      payment_condition_text1: [''],
      payment_condition_text2: [''],
      payment_condition_text3: [''],
      payment_condition_text4: [''],
      payment_text1: [''],
      payment_text2: [''],
      payment_text3: [''],
      payment_text4: [''],
      attachment_text2: [''],
      attachment_text1: [''],
      attachment_text3: [''],
      attachment_text4: [''],
      material_delivery1: [''],
      material_delivery2: [''],
      material_delivery3: [''],
      material_delivery4: [''],
      show_payment_condition_text1: [false],
      show_payment_condition_text2: [false],
      show_payment_condition_text3: [false],
      show_payment_condition_text4: [false],
      show_payment_text1: [false],
      show_payment_text2: [false],
      show_payment_text3: [false],
      show_payment_text4: [false],
      show_attachment_text1: [false],
      show_attachment_text2: [false],
      show_attachment_text3: [false],
      show_attachment_text4: [false],
    });
  }

  updateAssignmentSettingsData() {
    if (!this.canSaveUserFirmDetails) {
      return;
    }

    this.userFirmDetailsForm.markAllAsTouched();
    const { enabled, details } = this.userFirmDetailsForm.getRawValue();
    if (enabled && this.userFirmDetailsForm.controls.details.invalid) {
      return;
    }

    const changes = this.createUserPropertiesPatch(enabled, details);
    const firmSettings = this.stringifyFormValues(this.assignmentSettingsForm.value);
    if (Object.keys(changes).length === 0) {
      this.applyUserProperties(this.savedUserProperties);
      this.updateFirmAssignmentData.emit(firmSettings);
      return;
    }
    this.saveUserProperties(changes, firmSettings);
  }

  private createUserPropertiesPatch(
    enabled: boolean,
    details: Record<(typeof ASSIGNMENT_FIRM_DETAIL_FIELDS)[number], string>,
  ): UserPropertiesPatch {
    const hadOverride = this.hasUserFirmDetails(this.savedUserProperties);
    if (!enabled) {
      return hadOverride
        ? ASSIGNMENT_FIRM_DETAIL_FIELDS.reduce<UserPropertiesPatch>((changes, field) => {
            changes[`assignment.${field}`] = null;
            return changes;
          }, {})
        : {};
    }
    return ASSIGNMENT_FIRM_DETAIL_FIELDS.reduce<UserPropertiesPatch>((changes, field) => {
      const key: keyof UserAssignmentFirmDetails = `assignment.${field}`;
      if (!hadOverride || details[field] !== (this.savedUserProperties[key] ?? '')) {
        changes[key] = details[field];
      }
      return changes;
    }, {});
  }

  private saveUserProperties(changes: UserPropertiesPatch, firmSettings: FirmSettings): void {
    const requestId = crypto.randomUUID();
    this.savingUserFirmDetails.set(true);
    this.userFirmDetailsError.set(null);
    this.userFirmDetailsForm.disable();
    this.assignmentSettingsForm.disable();
    this.actions$
      .pipe(
        ofType(UserActions.patchCurrentUserPropertiesSucceeded, UserActions.patchCurrentUserPropertiesFailed),
        filter((action) => action.requestId === requestId),
        take(1),
        takeUntil(this.onDestroyBS),
        finalize(() => {
          this.savingUserFirmDetails.set(false);
          this.userFirmDetailsForm.enable();
          this.assignmentSettingsForm.enable();
        }),
      )
      .subscribe((action) => {
        if (action.type === UserActions.patchCurrentUserPropertiesSucceeded.type) {
          this.applyUserProperties(action.properties);
          this.updateFirmAssignmentData.emit(firmSettings);
        } else {
          this.userFirmDetailsError.set('Kunde inte spara dina egna byråuppgifter. Försök igen.');
        }
      });
    this.store.dispatch(
      UserActions.patchCurrentUserProperties({
        changes,
        requestId,
        userId: this.currentUser.id,
        firmCorporateIdentity: this.currentUser.firm.corporateIdentity,
      }),
    );
  }

  resetAssignmentSettings() {
    this.resetFirmAssignmentData.emit();
    this.resetDialog = false;
  }

  onSrfOrFarToggle(checked: boolean, id: string) {
    const useFar = this.assignmentSettingsForm.get('use_far');
    const useSrf = this.assignmentSettingsForm.get('use_srf');
    if (id === 'far') {
      useFar.setValue(checked);
      useSrf.setValue(false);
    } else if (id === 'srf') {
      useSrf.setValue(checked);
      useFar.setValue(false);
    }

    this.assignmentSettingsForm.get('srf_or_far').setValue(checked ? id : '');
  }

  private booleanifyFormValues(formValue: FirmSettings): FirmSettings {
    const booleanifiedFormValue = { ...formValue };
    Object.keys(booleanifiedFormValue).forEach((key) => {
      if (booleanifiedFormValue[key] === 'true' || booleanifiedFormValue[key] === 'false') {
        booleanifiedFormValue[key] = this.stringToBoolean(booleanifiedFormValue[key] as string);
      }
    });
    return booleanifiedFormValue;
  }

  private stringifyFormValues(formValue: FirmSettings): FirmSettings {
    const stringifiedFormValue = { ...formValue };
    Object.keys(stringifiedFormValue).forEach((key) => {
      if (typeof stringifiedFormValue[key] === 'boolean') {
        stringifiedFormValue[key] = this.booleanToString(stringifiedFormValue[key] as boolean);
      }
    });
    return stringifiedFormValue;
  }

  private booleanToString(value: boolean): string {
    return value ? 'true' : 'false';
  }

  private stringToBoolean(value: string): boolean {
    return value === 'true';
  }
}
